import { Schema } from "effect";
import { DesktopWorkspaceGrantIdSchema } from "./desktop-workspace";
import { compareUtf8Text, utf8ByteLength } from "./utf8";

export const DesktopFileSearchBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

export const DESKTOP_FILE_SEARCH_MAX_QUERY_CHARACTERS = 120;
export const DESKTOP_FILE_SEARCH_MAX_RELATIVE_PATH_BYTES = 4_096;
export const DESKTOP_FILE_SEARCH_MAX_DEPTH = 12;
export const DESKTOP_FILE_SEARCH_MAX_ENTRIES = 20_000;
export const DESKTOP_FILE_SEARCH_MAX_FILES = 2_000;
export const DESKTOP_FILE_SEARCH_MAX_FILE_BYTES = 1_048_576;
export const DESKTOP_FILE_SEARCH_MAX_TOTAL_BYTES = 33_554_432;
export const DESKTOP_FILE_SEARCH_MAX_LINE_BYTES = 8_192;
export const DESKTOP_FILE_SEARCH_MAX_SNIPPET_CHARACTERS = 240;
export const DESKTOP_FILE_SEARCH_MAX_RESULTS = 200;
export const DESKTOP_FILE_SEARCH_TIME_LIMIT_MS = 1_500;

const hasControlCharacter = (value: string): boolean => {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f)) return true;
  }
  return false;
};

const isCanonicalRelativePath = (value: string): boolean =>
  value !== "" &&
  !value.startsWith("/") &&
  !value.includes("\\") &&
  !hasControlCharacter(value) &&
  utf8ByteLength(value) <= DESKTOP_FILE_SEARCH_MAX_RELATIVE_PATH_BYTES &&
  value.split("/").every((segment) => segment !== "" && segment !== "." && segment !== "..");

const isAscii = (value: string): boolean =>
  Array.from(value).every((character) => (character.codePointAt(0) ?? 0) <= 0x7f);

const queryMatches = (match: string, query: string): boolean =>
  query === match ||
  (isAscii(query) && isAscii(match) && query.toLowerCase() === match.toLowerCase());

export const DesktopFileSearchQuerySchema = Schema.String.check(
  Schema.makeFilter(
    (value) =>
      (!hasControlCharacter(value) &&
        value.trim().length > 0 &&
        Array.from(value).length <= DESKTOP_FILE_SEARCH_MAX_QUERY_CHARACTERS) ||
      `must contain visible text within ${DESKTOP_FILE_SEARCH_MAX_QUERY_CHARACTERS} Unicode code points`,
  ),
);

export const DesktopFileSearchRelativePathSchema = Schema.String.check(
  Schema.makeFilter(
    (value) => isCanonicalRelativePath(value) || "must be a canonical relative file path",
  ),
);

const PositiveSafeIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(1),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);

const NonNegativeSafeIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);

export const DesktopFileSearchInputSchema = Schema.Struct({
  grantId: DesktopWorkspaceGrantIdSchema,
  query: DesktopFileSearchQuerySchema,
});

export const DesktopFileSearchMatchSchema = Schema.Struct({
  relativePath: DesktopFileSearchRelativePathSchema,
  line: PositiveSafeIntegerSchema,
  snippet: Schema.String.check(
    Schema.makeFilter(
      (value) =>
        Array.from(value).length <= DESKTOP_FILE_SEARCH_MAX_SNIPPET_CHARACTERS ||
        `must contain at most ${DESKTOP_FILE_SEARCH_MAX_SNIPPET_CHARACTERS} Unicode code points`,
    ),
  ),
  // Unicode code-point offsets keep Rust `char` indexing and renderer slicing identical.
  matchStart: NonNegativeSafeIntegerSchema,
  matchLength: PositiveSafeIntegerSchema,
}).check(
  Schema.makeFilter((match) => {
    const snippet = Array.from(match.snippet);
    return (
      match.matchStart + match.matchLength <= snippet.length ||
      "match range must be contained by the snippet"
    );
  }),
);

export const DesktopFileSearchResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  query: DesktopFileSearchQuerySchema,
  matches: Schema.Array(DesktopFileSearchMatchSchema).check(
    Schema.isMaxLength(DESKTOP_FILE_SEARCH_MAX_RESULTS),
  ),
  truncated: Schema.Boolean,
}).check(
  Schema.makeFilter((result) => {
    let previousPath: string | undefined;
    let previousLine = 0;
    for (const match of result.matches) {
      if (previousPath !== undefined) {
        const order = compareUtf8Text(match.relativePath, previousPath);
        if (order < 0 || (order === 0 && match.line <= previousLine)) {
          return "matches must be ordered by path and strictly increasing line";
        }
      }
      const matchedText = Array.from(match.snippet)
        .slice(match.matchStart, match.matchStart + match.matchLength)
        .join("");
      if (!queryMatches(matchedText, result.query)) {
        return "each match range must identify the requested query";
      }
      previousPath = match.relativePath;
      previousLine = match.line;
    }
    return true;
  }),
);

export const DesktopFileSearchErrorCodeSchema = Schema.Literals([
  "invalid-input",
  "invalid-response",
  "invalid-root",
  "root-not-granted",
  "search-failed",
  "unavailable",
]);

export const DesktopFileSearchErrorPayloadSchema = Schema.Struct({
  code: DesktopFileSearchErrorCodeSchema,
  message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(240)),
  retryable: Schema.Boolean,
});

export type DesktopFileSearchInput = typeof DesktopFileSearchInputSchema.Type;
export type DesktopFileSearchInputEncoded = typeof DesktopFileSearchInputSchema.Encoded;
export type DesktopFileSearchMatch = typeof DesktopFileSearchMatchSchema.Type;
export type DesktopFileSearchResult = typeof DesktopFileSearchResultSchema.Type;
export type DesktopFileSearchResultEncoded = typeof DesktopFileSearchResultSchema.Encoded;
export type DesktopFileSearchErrorCode = typeof DesktopFileSearchErrorCodeSchema.Type;
export type DesktopFileSearchErrorPayload = typeof DesktopFileSearchErrorPayloadSchema.Type;
export type DesktopFileSearchErrorPayloadEncoded =
  typeof DesktopFileSearchErrorPayloadSchema.Encoded;

export const decodeDesktopFileSearchInput = Schema.decodeUnknownSync(
  DesktopFileSearchInputSchema,
  DesktopFileSearchBoundaryParseOptions,
);
export const encodeDesktopFileSearchInput = Schema.encodeSync(
  DesktopFileSearchInputSchema,
  DesktopFileSearchBoundaryParseOptions,
);
export const decodeDesktopFileSearchResult = Schema.decodeUnknownSync(
  DesktopFileSearchResultSchema,
  DesktopFileSearchBoundaryParseOptions,
);
export const encodeDesktopFileSearchResult = Schema.encodeSync(
  DesktopFileSearchResultSchema,
  DesktopFileSearchBoundaryParseOptions,
);
export const decodeDesktopFileSearchErrorPayload = Schema.decodeUnknownSync(
  DesktopFileSearchErrorPayloadSchema,
  DesktopFileSearchBoundaryParseOptions,
);
export const encodeDesktopFileSearchErrorPayload = Schema.encodeSync(
  DesktopFileSearchErrorPayloadSchema,
  DesktopFileSearchBoundaryParseOptions,
);
